import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import { MatSort } from "@angular/material/sort";
import { RolesAndDefaultLandingPage } from "src/app/models/common";
import { Router } from "@angular/router";
import { ColumnType, FeedColumn } from "src/app/models/dco/dco-product-feed";

export function handleHeaderClick(event: MouseEvent, sort: MatSort): void {
  const target = event.target as HTMLElement;
  const isSortIcon = target.closest(".mat-sort-header-arrow");
  const isResizer = target.closest(".column-resizer");
  sort.disabled = !isSortIcon && !!isResizer;
  // sort.disabled = target.classList.contains('column-resizer')
}

export const downloadFile = (download_url) => {
  const anchor = document.createElement("a");
  anchor.href = download_url;
  anchor.target = "_blank";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

export const toProperCase = (string) => {
  return string.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

export function isEmpty(val: any): boolean {
  return val === null || val === undefined || val === "";
}

export function isInvalidAndOutOfRange(
  value: number,
  min?: number,
  max?: number
): boolean {
  return (
    value == null ||
    (min != null && value < min) ||
    (max != null && value > max)
  );
}

export const NAT_ASPECTS = [
  { label: "1200:627", ratio: 1200 / 627 },
  { label: "16:9", ratio: 16 / 9 },
  { label: "4:3", ratio: 4 / 3 },
];

export function isMatchingAspectRatio(width: number, height: number): boolean {
  const inputRatio = width / height;
  const threshold = 0.01;

  for (const aspect of NAT_ASPECTS) {
    if (Math.abs(inputRatio - aspect.ratio) < threshold) {
      return true;
    }
  }

  return false; // No matching aspect ratio found
}

export const isInvalidNumber = (value: number | null | undefined) => {
  return value == null || value === undefined || value < 0.1;
};

export const generateDSPRoleMap = (
  prefix: string,
  overrides: Partial<typeof RolesAndDefaultLandingPage> = {}
) => {
  const roleMap: any = {};
  for (const role in RolesAndDefaultLandingPage) {
    const custom = overrides[role as keyof typeof RolesAndDefaultLandingPage];
    const path =
      custom ??
      RolesAndDefaultLandingPage[
        role as keyof typeof RolesAndDefaultLandingPage
      ];
    const isPlatformRole =
      role === "platform_superadmin" || role === "finance_user";
    roleMap[role] = isPlatformRole ? `/${path}` : `/${prefix}/${path}`;
  }
  return roleMap;
};

export const getRoleAndDefaultPageMapping = (role: string) => {
  let roleAndDefaultPageMappings;
  let redirectPath;
  let params = {};
  const isTrue = (key: string) => localStorage.getItem(key) === "true";

  const apnxEnabled = isTrue("apnx_enabled");
  const bdspEnabled = isTrue("bdsp_enabled");
  const tdspEnabled = isTrue("tdsp_enabled");
  const adspEnabled = isTrue("adsp_enabled");

  if (tdspEnabled) {
    roleAndDefaultPageMappings = generateDSPRoleMap("tdsp");
  } else if (bdspEnabled) {
    roleAndDefaultPageMappings = generateDSPRoleMap("bdsp");
  } else if (adspEnabled) {
    roleAndDefaultPageMappings = generateDSPRoleMap("adsp");
    params["adsp_region"] = isTrue("adsp_us_region") ? "US" : "EU";
  } else if (apnxEnabled) {
    roleAndDefaultPageMappings = RolesAndDefaultLandingPage;
  } else {
    roleAndDefaultPageMappings = RolesAndDefaultLandingPage;
  }

  redirectPath = roleAndDefaultPageMappings[role];

  return { redirectPath, params };
};

export const setLocalStorageFlags = (source: any, keys: string[]) => {
  keys.forEach((key) => {
    if (key in source) {
      localStorage.setItem(key, source[key]);
    }
  });
};

export const setRegionFlags = (regions: any, isArray = false) => {
  if (isArray) {
    localStorage.setItem("adsp_us_region", regions?.includes("US") || false);
    localStorage.setItem("adsp_uk_region", regions?.includes("EU") || false);
  } else {
    localStorage.setItem("adsp_us_region", (regions === "US").toString());
    localStorage.setItem("adsp_uk_region", (regions === "EU").toString());
  }
};

export const setAggregateFlags = (items: any[]) => {
  if (!Array.isArray(items) || items.length === 0) return;

  const enabledKeys = Object.keys(items[0]).filter((key) =>
    key.endsWith("_enabled")
  );

  enabledKeys.forEach((key) => {
    const isEnabled = items.some((item) => item[key]);
    localStorage.setItem(key, isEnabled.toString());
  });
};

export const setEnabledPlatformFlags = (prefix: string, items: any[]) => {
  if (!Array.isArray(items) || items.length === 0) return;

  const enabledKeys = Object.keys(items[0]).filter((key) =>
    key.endsWith("_enabled")
  );

  enabledKeys.forEach((key) => {
    const flag = items.some((item) => item[key]) ? "true" : "false";
    const shortKey = key.replace("_enabled", ""); // e.g. apnx_enabled → apnx
    localStorage.setItem(`${prefix}_with_${shortKey}`, flag);
  });
};

export function onlyScriptOrImgTagValidator(): ValidatorFn {
  const imgRegex = /^<img\b[^>]*>$/i;

  const scriptRegex =
    /^<script\b[^>]*>([\s\S]*?)<\/script>(\s*<noscript\b[^>]*>[\s\S]*?<\/noscript>)?$/i;

  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const val = control.value.trim();

    if (imgRegex.test(val) || scriptRegex.test(val)) {
      return null;
    }

    return { onlyScriptOrImgTag: true };
  };
}

export function pixelTagValidator(pixelType: "js" | "image"): ValidatorFn {
  const imgRegex = /^<img\b[^>]*>$/i;
  const scriptRegex =
    /^<script\b[^>]*>([\s\S]*?)<\/script>(\s*<noscript\b[^>]*>[\s\S]*?<\/noscript>)?$/i;

  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || value.trim().length === 0) {
      return { invalidPixelTag: true };
    }

    const val = value.trim();

    if (pixelType === "js" && scriptRegex.test(val)) {
      return null; // valid script tag (with optional noscript)
    }

    if (pixelType === "image" && imgRegex.test(val)) {
      return null; // valid <img> tag
    }

    return { invalidPixelTag: true };
  };
}

export const countOccurrencesWithReduce = (
  arr,
  keyToCount: string
): Record<string, number> => {
  return arr.reduce((acc, current) => {
    const currentCount = acc[current[keyToCount]] || 0;
    return {
      ...acc,
      [current[keyToCount]]: currentCount + 1,
    };
  }, {} as Record<string, number>);
};

export const callback = (router: Router, url, globalParams) => {
  router.navigate([url], {
    queryParams: { ...globalParams },
  });
};

function isValidUrl(value: string): boolean {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

function isImageUrl(value: string): boolean {
  return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(value);
}

function isVideoUrl(value: string): boolean {
  return /\.(mp4|webm|mov|mkv)$/i.test(value);
}

function isAudioUrl(value: string): boolean {
  return /\.(mp3|wav|aac|ogg)$/i.test(value);
}

function detectColumnType(columnName: string, tableData: any[]): ColumnType {
  const sampleValues = tableData
    .slice(0, 2)
    .map((row) => row[columnName])
    .filter((v) => v !== null && v !== undefined && v !== "");

  // Value - based
  if (sampleValues.length) {
    // number
    if (sampleValues.every((v) => !isNaN(Number(v)))) {
      return "number";
    }

    // URL-based
    const urlValues = sampleValues.filter((v) => isValidUrl(v));

    if (urlValues.length) {
      if (urlValues.some((v) => isImageUrl(v))) {
        return "image";
      }

      if (urlValues.some((v) => isVideoUrl(v))) {
        return "video";
      }

      if (urlValues.some((v) => isAudioUrl(v))) {
        return "audio";
      }

      return "click_url";
    }

    return "text";
  }

  // Column name based fall back
  const name = columnName.toLowerCase();

  if (/(image|img|photo|logo)/.test(name)) return "image";
  if (/(video|mp4|movie)/.test(name)) return "video";
  if (/(audio|sound|mp3)/.test(name)) return "audio";
  if (/(url|link|click)/.test(name)) return "click_url";
  if (/(price|amount|qty|count)/.test(name)) return "number";

  return "text";
}

export const parseFeedColumns = (
  columns: Record<string, any>,
  tableData: any[]
): FeedColumn[] => {
  return Object.entries(columns).map(([key, value]) => ({
    name: value,
    type: detectColumnType(value, tableData),
  }));
};
